import type { ReadonlyDeep } from "type-fest";
import type { ZodType } from "zod";
import z from "zod";

import type { OrganisationIdUnbranded } from "./organisation.js";
import type { Person, PersonIdUnbranded } from "./person.js";
import type { UserIdUnbranded } from "./user.js";

/**
 * The name of the one event Haven publishes.
 *
 * Pusher names every event, and this is the name a subscriber binds to.
 */
export const RESOURCE_RELOAD = "resource-reload";

/**
 * A live notification, in full: a record changed, and nothing about what it
 * changed to.
 *
 * The absence of a payload is the point, not an omission to be filled in later.
 * A reader re-reads over the authenticated API, so no part of a young person's
 * record passes through the third party that carries these messages — which is
 * what makes carrying them there acceptable. Two further properties depend on
 * it: identical events on a channel are interchangeable, which is what lets the
 * publisher collapse them and the subscriber debounce them. Attaching a field
 * here would quietly remove both.
 */
export type ResourceReloadEvent = {
  type: typeof RESOURCE_RELOAD;
};

export type BrowserNotificationEvent = ResourceReloadEvent;

/**
 * The channel a change to each kind of record is announced on.
 *
 * A channel is a name both sides compute rather than a value either sends, so
 * these functions are the whole contract between a writer and a reader: a
 * publisher and a subscriber that call different ones fail by hearing nothing,
 * with no error on either side. Every channel a resource depends on therefore
 * comes from a function here, never from a name assembled at a call site.
 *
 * Every one is private. The `private-` marker is what makes Pusher ask the
 * server before admitting a subscriber, so without it holding the application
 * key — which every browser does — would be enough to watch any record whose
 * id you knew, and learn when a named child's record was touched.
 *
 * The marker has to stay at the front of the name once an environment prefix is
 * applied; `wireChannelName` is what keeps it there.
 */
export const notificationChannels = {
  person: (personId: PersonIdUnbranded) => `private-person.${personId}`,

  /**
   * Records appearing in, or leaving, one organisation's directory.
   *
   * Keyed on the organisation that registered a record, which is already what
   * governs who may reach it — so the channel follows the boundary the
   * authorisation model draws rather than introducing a second one.
   */
  personDirectory: (organisationId: OrganisationIdUnbranded) =>
    `private-person.directory.${organisationId}`,

  user: (userId: UserIdUnbranded) => `private-user.${userId}`,

  organisation: (organisationId: OrganisationIdUnbranded) =>
    `private-organisation.${organisationId}`,
};

/**
 * What a channel name names, for the server deciding whether to admit a
 * subscriber.
 *
 * A subscription request arrives carrying a channel name and nothing else, so
 * the name is the whole of what the decision can be made from. This union is
 * the inverse of the functions above: a name the publisher produces and this
 * cannot read is a channel nobody can ever subscribe to.
 */
export type NotificationSubject =
  | { kind: "person"; personId: PersonIdUnbranded }
  | { kind: "person-directory"; organisationId: OrganisationIdUnbranded }
  | { kind: "user"; userId: UserIdUnbranded }
  | { kind: "organisation"; organisationId: OrganisationIdUnbranded };

/**
 * The channel shapes this server publishes, and what each names.
 *
 * Literal patterns rather than ones built from strings: in a template literal a
 * `\.` is an unnecessary string escape that collapses to a bare `.`, which
 * matches any character — so a pattern assembled that way admits
 * `private-personX…` as a person.
 *
 * An id is any run without a separator, deliberately not a UUID check: parsing
 * the id is the business of whoever resolves it to a record, so this only has
 * to say which shape of name it is.
 */
const SUBJECTS: {
  pattern: RegExp;
  subject: (id: string) => NotificationSubject;
}[] = [
  {
    pattern: /^private-person\.directory\.([^.]+)$/,
    subject: (organisationId) => ({ kind: "person-directory", organisationId }),
  },
  {
    pattern: /^private-person\.([^.]+)$/,
    subject: (personId) => ({ kind: "person", personId }),
  },
  {
    pattern: /^private-user\.([^.]+)$/,
    subject: (userId) => ({ kind: "user", userId }),
  },
  {
    pattern: /^private-organisation\.([^.]+)$/,
    subject: (organisationId) => ({ kind: "organisation", organisationId }),
  },
];

/**
 * What `channel` names, or `undefined` for a name Haven does not publish.
 *
 * `undefined` must be refused rather than admitted: a channel nothing publishes
 * to cannot carry anything today, but admitting unknown names would make any
 * future channel readable by everybody the moment it was added.
 */
export const notificationSubject = (
  channel: string,
): NotificationSubject | undefined => {
  for (const { pattern, subject } of SUBJECTS) {
    const id = pattern.exec(channel)?.[1];
    if (id !== undefined) {
      return subject(id);
    }
  }

  return undefined;
};

/**
 * Every channel a reader of this young person must watch.
 *
 * A read of one record answers from its whole merge tree, so a change to any
 * record in that tree changes what this reader is shown. Deriving the channel
 * from the id in the request would miss the case that matters most: asking for
 * a record that has since been merged away answers with the record it now
 * belongs to, so the id asked for is not the id answered with — and the reader
 * looking at a record a colleague has just merged is the one most needing to be
 * told.
 *
 * The requested id needs no special handling: a merged-away record is carried
 * in the tree it was merged into, so its channel is already in this set.
 */
export const personChannels = (person: ReadonlyDeep<Person>): string[] => [
  notificationChannels.person(person.id),
  ...person.mergedPersonRecords.map((record) =>
    notificationChannels.person(record.id),
  ),
];

/**
 * The directories this young person appears in.
 *
 * More than one where a merge has joined records registered by different
 * organisations: each of those organisations still lists what it registered, so
 * each of their directories is affected by a change to the tree.
 */
export const personDirectoryChannels = (
  person: ReadonlyDeep<Person>,
): string[] => [
  ...new Set(
    [
      person.createdByOrganisationId,
      ...person.mergedPersonRecords.map(
        (record) => record.createdByOrganisationId,
      ),
    ].map(notificationChannels.personDirectory),
  ),
];

/**
 * Every channel a write to this young person must announce on.
 *
 * Wider than what any single reader watches, deliberately: a write notifies
 * everything whose answer it could have changed, because an event costs a
 * request and the alternative is somebody reading a record that is quietly
 * wrong. The directories are included for every write rather than only for the
 * ones that add or remove a record, so a listing stays correct without each of
 * its rows holding a subscription of its own.
 */
export const personNotificationChannels = (
  person: ReadonlyDeep<Person>,
): string[] => [...personChannels(person), ...personDirectoryChannels(person)];

/**
 * What a browser needs in order to receive live updates, as the server reports
 * it.
 *
 * Told to the client rather than built into it, so the prefix a subscriber
 * applies is by construction the one the publisher applies. Configuring each
 * end separately makes a mismatch possible, and a mismatch is silent on both
 * sides — every event is published and none is ever heard.
 *
 * `enabled: false` is a complete answer: an environment with no Pusher
 * credentials is one where nothing is published, and a client that knows that
 * can say a page is not live rather than appearing to be.
 */
export type RealtimeSettings =
  | { enabled: false }
  | {
      enabled: true;
      /** Identifies the application. Public by design: every subscriber holds it. */
      clientKey: string;
      cluster: string;
      /** Absent where channels are not namespaced. */
      channelPrefix?: string;
    };

export const RealtimeSettingsSchema = z.discriminatedUnion("enabled", [
  z.object({ enabled: z.literal(false) }),
  z.object({
    enabled: z.literal(true),
    clientKey: z.string().min(1),
    cluster: z.string().min(1),
    channelPrefix: z.string().min(1).optional(),
  }),
]) satisfies ZodType<RealtimeSettings>;

export type RealtimeAuthorisationRequestUnbranded = {
  socketId: string;
  channelName: string;
};

export const RealtimeAuthorisationRequestSchema = z
  .object({
    socketId: z.string().min(1),
    // Pusher's own limit on the length of a channel name.
    channelName: z.string().min(1).max(164),
  })
  .brand<"haven-common:RealtimeAuthorisationRequest">() satisfies ZodType<RealtimeAuthorisationRequestUnbranded>;

/**
 * What a browser sends to be admitted to one channel.
 *
 * `socketId` identifies the connection the token is minted for, which is what
 * stops a token being passed to a different browser: Pusher checks the
 * signature against the socket presenting it.
 *
 * The channel name is the one the browser subscribes to, carrying the
 * environment's namespace, because that is the name the signature covers. The
 * server refuses a name from any other namespace.
 */
export type RealtimeAuthorisationRequest = z.infer<
  typeof RealtimeAuthorisationRequestSchema
>;

export type RealtimeAuthorisationUnbranded = {
  auth: string;
};

export const RealtimeAuthorisationSchema = z.object({
  auth: z.string().min(1),
}) satisfies ZodType<RealtimeAuthorisationUnbranded>;

/**
 * The token Pusher accepts as proof that this server admitted this socket to
 * this channel. Opaque to the client, which passes it straight on.
 */
export type RealtimeAuthorisation = z.infer<typeof RealtimeAuthorisationSchema>;
